import { CartComponentBase } from '@utilities/cart-component-base';
import { EVENTS } from '@utilities/events';
import { formatMoneyFromCurrency } from '@utilities/theme';

if (!customElements.get("cart-discounts")) {
  /**
   * Cart Discounts Web Component
   *
   * A custom element that manages the display of cart discount information,
   * including discount applications list with animations and discount aggregation.
   *
   * Features:
   * - Single-line or multi-line rendering modes via component configuration
   * - Groups discounts by title and shows aggregate savings
   * - Smooth fade in/out animations when adding or removing discount items
   * - Money formatting integration
   * - Change detection to avoid unnecessary updates
   * - Uses cart state for accurate discount information
   *
   * Configuration:
   * - displayMode: "single-line" | "multi-line" (default: "single-line")
   *   - single-line: Shows only "Total Savings" with final discount total
   *   - multi-line: Shows each discount type as an aggregated line
   *
   * Data Sources:
   * - Cart state from cart-state:changed events
   *
   * Usage:
   * Simply wrap your discount HTML structure with <cart-discounts>:
   *
   * <cart-discounts id="CartDiscounts-{{ section.id }}">
   *   <script id="CartDiscounts-{{ section.id }}__json" component-config type="application/json">
   *     {
   *       "displayMode": "single-line"
   *     }
   *   </script>
   *   <ul data-cart-discounts-list>
   *     <!-- Discount items will be managed automatically -->
   *   </ul>
   * </cart-discounts>
   *
   * The component expects a container with [data-cart-discounts-list] attribute
   * and will automatically populate it with discount items that have the structure:
   *
   * <li class="cart-discounts__list__item">
   *   <span class="cart-discounts__list__item__title">Discount Name</span>
   *   <span class="cart-discounts__list__item__savings">Saving $X.XX</span>
   * </li>
   *
   * @class CartDiscounts
   * @extends CartComponentBase
   */
  class CartDiscounts extends CartComponentBase {
    constructor() {
      super();
      /** @type {string} Active currency code from cart state */
      this.currency = 'USD';
      /** @type {number|null} Last known discount count for change detection */
      this.lastDiscountCount = null;
      /** @type {number|null} Last known total discount for change detection */
      this.lastTotalDiscount = null;
      /** @type {Object|null} Last known cart state for comparison */
      this.lastState = null;
      /** @type {Object|null} Last known compare at price totals for change detection */
      this.lastCompareAtPriceTotals = null;
      /** @type {number|null} Last known final discount savings amount for change detection */
      this.lastFinalDiscountAmount = null;

      // Bind event handlers
      this.handleStateChange = this.handleStateChange.bind(this);
    }

    /**
     * Handle cart state changes
     * @param {Object} data - The cart state change data
     */
    async handleStateChange(data) {
      const { state, changes } = data;

      if (this.allowDebug()) {
        console.log("CartDiscounts: Received cart-state:changed event:", {
          hasState: !!state,
          hasChanges: !!(changes && Object.keys(changes).length > 0),
          hasDiscountChanges: !!(changes && (changes.totalDiscount || changes.discountApplications || changes.discountsAdded || changes.discountsRemoved)),
          totalDiscount: state?.totalDiscount,
          discountApplications: state?.discountApplications,
          discountCount: state?.discountApplications?.length || 0,
          compareAtPriceTotals: state?.compareAtPriceTotals,
          hasCompareAtPriceSavings: (state?.compareAtPriceTotals?.compareAtPriceDiscount || 0) > 0
        });
      }

      // Update currency from state
      if (state.currency) {
        this.currency = state.currency;
      }

      // Store last state for comparison
      this.lastState = state;

      // Update discount display
      await this.#updateDiscountDisplay(state);
    }

    /**
     * Handles cart-state:refreshed event to force discount aggregation and display re-render
     * @param {Object} data - The cart state refreshed data
     * @private
     */
    #handleStateRefreshed(data) {
      if (this.allowDebug()) {
        console.log("CartDiscounts: Received cart-state:refreshed event, forcing discount display refresh");
      }

      const state = data?.state;
      if (!state) {
        if (this.allowDebug()) {
          console.warn("CartDiscounts: No state provided in cart-state:refreshed event");
        }
        return;
      }

      // Reset tracking variables to force a full re-render
      this.lastDiscountCount = null;
      this.lastTotalDiscount = null;
      this.lastCompareAtPriceTotals = null;
      this.lastFinalDiscountAmount = null;

      // Force full re-render with current state
      this.#updateDiscountDisplay(state);
    }

    /**
     * Updates the discount display elements using flexible selectors
     * Syncs discount applications list and updates data attributes for CSS styling
     * In single-line mode: Shows only "Total Savings" with final discount total
     * In multi-line mode: Groups discounts by title and shows aggregate savings for each unique discount
     * Uses smooth fade in/out animations when adding or removing discount items
     * @param {Object} state - Current cart state
     * @private
     */
    async #updateDiscountDisplay(state) {
      if (!state) return;

      const displayMode = this.config?.displayMode || 'single-line';

      if (this.allowDebug()) {
        console.log("CartDiscounts: Display mode:", displayMode);
      }

      // Handle single-line mode
      if (displayMode === 'single-line') {
        await this.#updateSingleLineDisplay(state);
        return;
      }

      // Handle multi-line mode (existing behavior)
      const discountApplications = state.discountApplications || [];
      const totalDiscount = state.totalDiscount || 0;
      const compareAtPriceTotals = state.compareAtPriceTotals || null;

      // Check if discount data has actually changed to avoid unnecessary updates
      const currentDiscountCount = discountApplications.length;
      const currentTotalDiscount = totalDiscount;
      const currentCompareAtPriceTotals = compareAtPriceTotals ? JSON.stringify(compareAtPriceTotals) : null;

      if (this.lastDiscountCount === currentDiscountCount &&
          this.lastTotalDiscount === currentTotalDiscount &&
          JSON.stringify(this.lastCompareAtPriceTotals) === currentCompareAtPriceTotals) {
        // No changes in discount data, skip update
        return;
      }

      // Update tracking variables
      this.lastDiscountCount = currentDiscountCount;
      this.lastTotalDiscount = currentTotalDiscount;
      this.lastCompareAtPriceTotals = compareAtPriceTotals;

      if (this.allowDebug()) {
        console.log("CartDiscounts: Updating discount display", {
          totalDiscount: totalDiscount,
          discountApplications: discountApplications,
          discountCount: currentDiscountCount,
          compareAtPriceTotals: compareAtPriceTotals,
          hasCompareAtPriceSavings: (compareAtPriceTotals?.compareAtPriceDiscount || 0) > 0,
          hasState: !!state
        });
      }

      // Update discount applications list
      const discountsList = this.querySelector("[data-cart-discounts-list]");
      if (discountsList) {
        const currentDiscounts = discountApplications;

        if (this.allowDebug()) {
          console.log("CartDiscounts: Raw discount applications", currentDiscounts);
          console.log("CartDiscounts: Discount count:", currentDiscounts.length);
        }

        // Group discounts by title and calculate total savings for each
        const discountGroups = new Map();

        currentDiscounts.forEach((discount, index) => {
          const title = discount.title;
          const dollarValue = Math.abs(discount.value || 0); // Discount value comes in dollars
          const centsValue = Math.round(dollarValue * 100); // Convert to cents for formatMoneyFromCurrency utility

          if (this.allowDebug()) {
            console.log(`CartDiscounts: Processing discount ${index + 1}/${currentDiscounts.length}`, {
              title,
              dollarValue,
              centsValue,
              originalDiscount: discount,
              currentMapValue: discountGroups.get(title) || 0,
              hasExistingEntry: discountGroups.has(title)
            });
          }

          if (discountGroups.has(title)) {
            const previousValue = discountGroups.get(title);
            const newValue = previousValue + centsValue;
            discountGroups.set(title, newValue);

            if (this.allowDebug()) {
              console.log(`CartDiscounts: Aggregated "${title}": ${previousValue} + ${centsValue} = ${newValue}`);
            }
          } else {
            discountGroups.set(title, centsValue);

            if (this.allowDebug()) {
              console.log(`CartDiscounts: First entry for "${title}": ${centsValue}`);
            }
          }
        });

        // Add compare at price savings as "Sale Item Savings" if there are any
        if (compareAtPriceTotals && (compareAtPriceTotals.compareAtPriceDiscount || 0) > 0) {
          const saleItemsTitle = "Sale Item Savings";
          const saleItemsDiscountMoney = compareAtPriceTotals.compareAtPriceDiscountMoney || "$0.00";

          if (this.allowDebug()) {
            console.log("CartDiscounts: Adding compare at price savings as sale items", {
              title: saleItemsTitle,
              dollarSavings: compareAtPriceTotals.compareAtPriceDiscount,
              formattedSavings: saleItemsDiscountMoney
            });
          }

          // Use a special key to store pre-formatted money for compare at price savings
          discountGroups.set(saleItemsTitle, saleItemsDiscountMoney);
        }

        if (this.allowDebug()) {
          console.log("CartDiscounts: Final grouped discounts", {
            groups: Array.from(discountGroups.entries()),
            mapSize: discountGroups.size
          });
        }

        // Animate discount list changes instead of jarring replacement
        await this.#animateDiscountListChanges(discountsList, discountGroups);
      }
    }

    /**
     * Updates the discount display in single-line mode
     * Shows only "Total Savings" with the final discount total
     * @param {Object} state - Current cart state
     * @private
     */
    async #updateSingleLineDisplay(state) {
      const finalDiscountTotals = state?.finalDiscountTotals || null;
      const finalDiscountAmount = finalDiscountTotals?.finalDiscount || 0;
      const finalDiscountMoney = finalDiscountTotals?.finalDiscountMoney || "$0";

      // Check if the value has actually changed to avoid unnecessary updates
      if (this.lastFinalDiscountAmount === finalDiscountAmount) {
        return;
      }

      // Update tracking variable
      this.lastFinalDiscountAmount = finalDiscountAmount;

      if (this.allowDebug()) {
        console.log("CartDiscounts: Updating single-line display", {
          finalDiscountAmount,
          finalDiscountMoney,
          finalDiscountTotals
        });
      }

      // Update discount applications list with single "Total Savings" entry
      const discountsList = this.querySelector("[data-cart-discounts-list]");
      if (discountsList) {
        const discountGroups = new Map();
        const singleLineHeading = this.config?.singleLineHeading || "Total Savings";

        // Only show "Total Savings" if finalDiscountAmount is greater than 0
        if (finalDiscountAmount > 0) {
          discountGroups.set(singleLineHeading, finalDiscountMoney);
        }

        await this.#animateDiscountListChanges(discountsList, discountGroups);
      }
    }

    /**
     * Animates discount list changes with smooth fade in/out transitions
     * Uses a simple rendered/not-rendered state for cleaner animation logic
     * @param {HTMLElement} discountsList - The discount list container
     * @param {Map} newDiscountGroups - Map of new discount titles to values
     * @private
     */
    async #animateDiscountListChanges(discountsList, newDiscountGroups) {
      // Get current discount items and their titles
      const currentItems = Array.from(discountsList.querySelectorAll('.cart-discounts__list__item'));
      const currentTitles = new Set();

      // Extract titles from existing items
      currentItems.forEach(item => {
        const titleElement = item.querySelector('.cart-discounts__list__item__title');
        if (titleElement) {
          currentTitles.add(titleElement.textContent.trim());
        }
      });

      const newTitles = new Set(newDiscountGroups.keys());

      if (this.allowDebug()) {
        console.log("CartDiscounts: Animating discount changes", {
          currentTitles: [...currentTitles],
          newTitles: [...newTitles],
          currentItemCount: currentItems.length,
          newItemCount: newDiscountGroups.size
        });
      }

      // Determine which items need to be removed and which need to be added
      const itemsToRemove = currentItems.filter(item => {
        const titleElement = item.querySelector('.cart-discounts__list__item__title');
        const title = titleElement ? titleElement.textContent.trim() : '';
        return !newTitles.has(title);
      });

      const titlesToAdd = [...newTitles].filter(title => !currentTitles.has(title));

      // Step 1: Fade out items that need to be removed
      if (itemsToRemove.length > 0) {
        if (this.allowDebug()) {
          console.log("CartDiscounts: Fading out discount items", {
            count: itemsToRemove.length,
            titles: itemsToRemove.map(item => item.querySelector('.cart-discounts__list__item__title')?.textContent)
          });
        }

        // Remove rendered class to trigger fade out
        itemsToRemove.forEach((item, index) => {
          setTimeout(() => {
            item.classList.remove('cart-discounts__list__item--rendered');
          }, index * 50); // Stagger the fade out by 50ms
        });

        // Wait for fade out to complete
        await new Promise(resolve => {
          setTimeout(() => {
            // Remove items from DOM
            itemsToRemove.forEach(item => {
              if (item.parentNode) {
                item.remove();
              }
            });
            resolve();
          }, 300 + (itemsToRemove.length * 50)); // Wait for animations + stagger
        });
      }

      // Step 2: Update existing items that have changed values
      const remainingItems = discountsList.querySelectorAll('.cart-discounts__list__item');
      for (const item of remainingItems) {
        const titleElement = item.querySelector('.cart-discounts__list__item__title');
        const savingsElement = item.querySelector('.cart-discounts__list__item__savings');

        if (titleElement && savingsElement) {
          const title = titleElement.textContent.trim();
          const newValue = newDiscountGroups.get(title);

          if (newValue !== undefined) {
            let newSavingsText;

            // Check if this is pre-formatted money (string values)
            if (typeof newValue === 'string') {
              newSavingsText = `Saving ${newValue}`;
            } else {
              // Regular discount - format using formatMoneyFromCurrency
              try {
                const formattedSavings = formatMoneyFromCurrency(newValue, this.currency);
                newSavingsText = `Saving ${formattedSavings}`;
              } catch (error) {
                this._handleError(error, 'formatting updated discount amount');
                newSavingsText = `Saving $${(newValue / 100).toFixed(2)}`;
              }
            }

            // Only update if the value has changed
            if (savingsElement.textContent !== newSavingsText) {
              if (this.allowDebug()) {
                console.log("CartDiscounts: Updating discount value", {
                  title,
                  oldText: savingsElement.textContent,
                  newText: newSavingsText,
                  isPreFormatted: typeof newValue === 'string'
                });
              }
              savingsElement.textContent = newSavingsText;
            }
          }
        }
      }

      // Step 3: Add new items with fade in animation
      if (titlesToAdd.length > 0) {
        if (this.allowDebug()) {
          console.log("CartDiscounts: Adding new discount items", {
            count: titlesToAdd.length,
            titles: titlesToAdd
          });
        }

        for (const [index, title] of titlesToAdd.entries()) {
          const totalValue = newDiscountGroups.get(title);

          // Format the savings amount
          let formattedSavings = '';

          // Check if this is pre-formatted money (string values)
          if (typeof totalValue === 'string') {
            formattedSavings = totalValue;
          } else if (totalValue > 0) {
            // Regular discount - format using formatMoneyFromCurrency
            try {
              formattedSavings = formatMoneyFromCurrency(totalValue, this.currency);
            } catch (error) {
              this._handleError(error, 'formatting new discount amount');
              formattedSavings = `$${(totalValue / 100).toFixed(2)}`;
            }
          }

          // Create new discount item (starts invisible due to default opacity: 0)
          const listItem = document.createElement('li');
          listItem.className = 'cart-discounts__list__item';
          listItem.innerHTML = `
            <span class="cart-discounts__list__item__title">${title}</span>
            <span class="cart-discounts__list__item__savings">Saving ${formattedSavings}</span>
          `;

          // Add to DOM
          discountsList.appendChild(listItem);

          // Trigger fade in after a short delay to ensure DOM is ready
          setTimeout(() => {
            listItem.classList.add('cart-discounts__list__item--rendered');
          }, 50 + (index * 50)); // Stagger the fade in by 50ms
        }
      }
    }

    /**
     * Override the base class event listener setup
     * @protected
     */
    _setupEventListeners() {
      this._subscribe(EVENTS.CART_STATE_CHANGED, this.handleStateChange);
      this._subscribe(EVENTS.CART_STATE_REFRESHED, this.#handleStateRefreshed.bind(this));

      if (this.allowDebug()) {
        console.log("CartDiscounts: Event listeners set up for state updates");
      }
    }

    /**
     * Override the base class component-specific initialization
     * @protected
     */
    async _initializeComponent() {
      // Process discount display on initial load to aggregate duplicates
      const initialState = this.cartStateManager?.getState();

      if (initialState) {
        if (this.allowDebug()) {
          console.log("CartDiscounts: Processing initial discount display", {
            hasState: !!initialState,
            discountCount: initialState?.discountApplications?.length || 0,
            hasCompareAtPriceSavings: (initialState?.compareAtPriceTotals?.compareAtPriceDiscount || 0) > 0,
            compareAtPriceTotals: initialState?.compareAtPriceTotals,
            finalDiscountTotals: initialState?.finalDiscountTotals
          });
        }

        // Update currency and store last state for comparison
        this.currency = initialState.currency || 'USD';
        this.lastState = initialState;

        await this.#updateDiscountDisplay(initialState);
      }

      if (this.allowDebug()) {
        console.log("CartDiscounts: Component initialized successfully");
      }
    }

    /**
     * Override base class cleanup to include component-specific cleanup
     * @protected
     */
    _cleanup() {
      // Remove event listeners
      document.removeEventListener("cart-state:changed", this.handleStateChange);

      // Reset tracking variables
      this.lastDiscountCount = null;
      this.lastTotalDiscount = null;
      this.lastState = null;
      this.lastCompareAtPriceTotals = null;
      this.lastFinalDiscountAmount = null;

      // Call base class cleanup
      super._cleanup();

      if (this.allowDebug()) {
        console.log("CartDiscounts: Cleaned up component-specific listeners and references");
      }
    }
  }

  customElements.define("cart-discounts", CartDiscounts);
}
